// EventList.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import axiosClient from '../api/axiosClient';
import { useAuth } from '../context/AuthContext';
import EventCard from './EventCard';
import FilterForm from './FilterForm';
import { getMunicipality } from '../utils/GetMunicipality';
import Typography from './Typography';
import { theme } from '../theme';
import SpinnerCircular from './SpinnerCircular';
import { useTranslation } from 'react-i18next';
import Button from './Button/Button';
import SearhFailIcon from './Icons/SearchFailIcon';
import { EXPERIENCE_LEVELS, TICKET_FREE, TICKET_PAID } from '../constants/eventTypes';

const EventList = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tEventList } = useTranslation('eventList')

  const navigate = useNavigate();
  const { user } = useAuth();

  const [events, setEvents] = useState([]);
  const [filters, setFilters] = useState({
    typology: [],
    timeFilter: 'flexible',
    location: JSON.parse(localStorage.getItem('location')) || { lat: 40.4378, lng: -3.8443, address: 'Madrid' }, // Valores por defecto para Madrid
    radius: 1000,
    terrain: [],
    experience: [],
    ticketType: [],
    needsVehicle: [],
    vehicleTypes: []
  });

  const [municipality, setMunicipality] = useState(localStorage.getItem('municipality') || 'Madrid');
  const [isLoading, setIsLoading] = useState(false);

  const handleCreateEvent = () => {
    const isAuthenticated = !!user;
    if (isAuthenticated) {
      navigate('/create-event'); // Redirigir a la página de creación de eventos si está autenticado
    } else {
      navigate('/signin'); // Redirigir al login si no está autenticado
    }
  };

  const fetchEvents = async (currentFilters) => {
    setIsLoading(true);
    try {
      // Construir objeto de parámetros básicos
      const params = {
        lat: currentFilters.location.lat,
        lng: currentFilters.location.lng,
        radius: currentFilters.radius,
      };

      // Para eventTypes (typology), enviar como array
      if (currentFilters.typology && currentFilters.typology.length > 0) {
        params.eventTypes = currentFilters.typology;
      }

      if (currentFilters.timeFilter && currentFilters.timeFilter !== 'flexible') {
        params.timeFilter = currentFilters.timeFilter;
      }

      if (currentFilters.terrain.length > 0) {
        // Si se selecciona "all" en terreno no filtramos
        const terrainArray = [...currentFilters.terrain];
        if (!terrainArray.includes('all')) {
          params.terrain = terrainArray;
        }
      }

      // Para experience: si se seleccionan todos, no se filtra
      if (currentFilters.experience && currentFilters.experience.length > 0) {
        const allExp = EXPERIENCE_LEVELS;
        if (!(allExp.every(level => currentFilters.experience.includes(level)))) {
          params.experience = currentFilters.experience;
        }
      }

      // Para ticketType: si se seleccionan ambos, no se filtra
      if (currentFilters.ticketType.length > 0) {
        // Si se han seleccionado ambos tipos ("free" y "paid"), no filtramos
        if (!(currentFilters.ticketType.includes(TICKET_FREE) && currentFilters.ticketType.includes(TICKET_PAID))) {
          params.ticketType = currentFilters.ticketType;
        }
      }

      // Para needsVehicle
      if (currentFilters.needsVehicle && currentFilters.needsVehicle.length > 0) {
        if (currentFilters.needsVehicle.includes('true') && currentFilters.needsVehicle.includes('false')) {
          // No se añade filtro
        } else if (currentFilters.needsVehicle.includes('true')) {
          params.needsVehicle = true;
        } else if (currentFilters.needsVehicle.includes('false')) {
          params.needsVehicle = false;
        }
      }

      // Para vehicleTypes
      if (currentFilters.vehicleTypes && currentFilters.vehicleTypes.length > 0) {
        params.vehicleTypes = currentFilters.vehicleTypes;
      }


      // console.log('Enviando parámetros al backend:', params);
      const response = await axiosClient.get('/api/events', { params });
      // const response = await axiosClient.get('/events', { params });
      // console.log('Respuesta del backend:', response.data);

      // Si response.data.events no es array, forzamos un array vacío
      setEvents(Array.isArray(response.data.events) ? response.data.events : []);
      // setEvents(response.data.events);
    } catch (error) {
      console.error(
        'Error fetching events:',
        error.response ? error.response.data : error.message
      );
      // En caso de error en la petición, garantizamos que events sea array
      setEvents([]);

    } finally {
      setIsLoading(false);
    }
  };

  // // Llamar a fetchEvents al montar el componente
  // useEffect(() => {
  //   // console.log('Component mounted, fetching initial events.');
  //   fetchEvents(filters);
  // }, []);

  // Actualizar eventos al cambiar los filtros
  useEffect(() => {
    // console.log('Filters updated:', filters);
    fetchEvents(filters);
  }, [filters]);

  useEffect(() => {
    if (!localStorage.getItem('location')) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const userLocation = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            };

            const geocoder = new window.google.maps.Geocoder();

            geocoder.geocode({ location: userLocation }, (results, status) => {
              if (status === 'OK') {
                if (results[0]) {
                  const addressData = results[0];
                  const userAddress = addressData.formatted_address || 'Dirección desconocida';
                  const userMunicipality = getMunicipality(addressData.address_components || []);
                  console.log('User municipality from geocoding:', userMunicipality);

                  const completeUserLocation = {
                    ...userLocation,
                    address: userAddress,
                  };

                  localStorage.setItem('location', JSON.stringify(completeUserLocation));
                  localStorage.setItem('municipality', userMunicipality);

                  setFilters((prevFilters) => ({
                    ...prevFilters,
                    location: completeUserLocation,
                  }));
                  setMunicipality(userMunicipality);
                } else {
                  console.error('No se encontraron resultados para la geocodificación.');
                }
              } else {
                console.error('Geocoder failed debido a: ' + status);
              }
            });
          },
          (error) => {
            if (error.code === 2) {
              navigator.geolocation.getCurrentPosition(
                (position) => {
                  const userLocation = {
                    lat: position.coords.latitude,
                    lng: position.coords.longitude,
                  };
                  const geocoder = new window.google.maps.Geocoder();
                  geocoder.geocode({ location: userLocation }, (results, status) => {
                    if (status === 'OK' && results[0]) {
                      const addressData = results[0];
                      const userAddress = addressData.formatted_address || 'Dirección desconocida';
                      const userMunicipality = getMunicipality(addressData.address_components || []);
                      const completeUserLocation = { ...userLocation, address: userAddress };
                      localStorage.setItem('location', JSON.stringify(completeUserLocation));
                      localStorage.setItem('municipality', userMunicipality);
                      setFilters((prevFilters) => ({ ...prevFilters, location: completeUserLocation }));
                      setMunicipality(userMunicipality);
                    }
                  });
                },
                () => {},
                { enableHighAccuracy: false, timeout: 10000, maximumAge: 60000 }
              );
            }
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      } else {
        console.error('Geolocalización no es soportada por este navegador.');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resetea sólo los filtros "otros" y de tipología
  const handleResetAllFilters = () => {
    setFilters(prev => ({
      ...prev,
      typology: [],       // event types
      terrain: [],        // terrenos
      experience: [],     // niveles
      ticketType: [],     // free/paid
      needsVehicle: []    // vehicle required
    }));
  };


  return (
    <>
      <FilterForm
        filters={filters}
        setFilters={setFilters}
        municipality={municipality}
        setMunicipality={setMunicipality}
      />
      <>
        {isLoading ? (
          <EventEmptyWapper>
            <LoaderMsg>
              <SpinnerCircular size={30} thickness={180} speed={100} color={theme.fill.brandMain} secondaryColor={theme.fill.defaultWeak} />
              <Typography as="h3" $variant="body-1-semibold" color={theme.colors.defaultWeak} style={{ paddingRight: "4px" }}>
                {tCommon('loadingEvents')}
              </Typography>
            </LoaderMsg>
          </EventEmptyWapper>
        ) : events.length === 0 ? (
          !Array.isArray(events) ? (
            <EventEmptyWapper>
              <Typography as="h3" $variant="body-1-semibold"
                color={theme.colors.defaultWeak}>
                {tEventList('loadingEventsError')}
                {/* “Error cargando eventos” o un texto en i18n */}
              </Typography>
            </EventEmptyWapper>
          ) : events.length === 0 ? (
            <EventNoResultsWrapper>
              <ResultContainer>
                <ResultsMessage>
                  <ResultsTextWrapper>
                    <SearhFailIcon fill={theme.colors.defaultSubtle} />
                    <Typography as="h3" $variant="body-1-semibold" $align="center" color={theme.colors.defaultStrong}>
                      {tEventList('emptyState.noEvents.errorTitle')}
                      {/* No se encontraron eventos */}
                    </Typography>
                    <Typography as="p" $variant="body-2-regular" $align="center" color={theme.colors.defaultWeak} style={{ maxWidth: "300px" }}>
                      {tEventList('emptyState.noEvents.errorDescription')}
                      {/* Prueba a modificar los filtros o ampliar el radio de búsqueda para ver más resultados. */}
                    </Typography>
                  </ResultsTextWrapper>
                  <Button $variant="ghost" onClick={handleResetAllFilters}>
                    {tEventList('emptyState.noEvents.resetFilter')}
                    {/* Resetear filtros */}
                  </Button>
                </ResultsMessage>
                <ResultsActions>
                  <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong} $align="center">
                    {tEventList('emptyState.createEvent.title')}
                    {/* ¿No encuentras lo que buscas? */}
                  </Typography>
                  <Button $variant="outline" onClick={handleCreateEvent}>
                    {tEventList('emptyState.createEvent.action')}
                    {/* Crea un evento */}
                  </Button>
                </ResultsActions>
              </ResultContainer>
            </EventNoResultsWrapper>
          ) : (
            <EventsGrid>
              {events.map(event => (
                <EventCard
                  key={event.id || event._id.toString()}
                  event={{ ...event, id: event.id || event._id.toString() }}
                />
              ))}
            </EventsGrid>
          )
        ) : (
          <EventsGrid>
            {events.map(event => (
              <EventCard
                key={event.id || event._id.toString()}
                event={{ ...event, id: event.id || event._id.toString() }}
              />
            ))}
          </EventsGrid>
        )}
      </>
    </>
  );
};

export default EventList;

const EventsGrid = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.sizing.md};
  grid-auto-flow: row;
  grid-template-columns: 1fr 1fr 1fr;
  grid-template-rows: auto;
  margin-left: auto;
  margin-right: auto;
  max-width: 1400px;
  padding: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.sizing.sm};
    grid-template-columns: 1fr;
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const EventEmptyWapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.sizing.sm};
  }
`;

const EventNoResultsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm};
  padding-top: ${({ theme }) => theme.sizing.md};
`;

const ResultContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.sm};
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;

const ResultsMessage = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultMain};
  border-bottom: 1px solid ${({ theme }) => theme.fill.defaultWeak};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.sm};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.lg};
  padding-bottom: ${({ theme }) => theme.sizing.md};
  width: 100%;
`;

const ResultsTextWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: center;
`;

const ResultsActions = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaultSubtle};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.md};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    row-gap: ${({ theme }) => theme.sizing.sm};
    width: 100%;
  }
`;

const LoaderMsg = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.fill.defaulMain};
  border: 1px solid ${({ theme }) => theme.fill.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.sm};
  bottom: ${({ theme }) => theme.radius.md};
  box-shadow: 0 0 8px -4px ${({ theme }) => theme.fill.inverseAlphaMain24};
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.sizing.xs};
  justify-content: center;
  padding: ${({ theme }) => theme.sizing.xs}; 
  position: absolute;
`;


